import { useCallback, useEffect, useRef, useState } from "react";
import SurveyPlayer from "./SurveyPlayer";
import { surveyApi as defaultApi } from "./api";
import "./SurveyGate.css";

const laterKey = (userId) => `r21-survey-later:${userId}`;

function readLater(userId) {
  try {
    return sessionStorage.getItem(laterKey(userId)) === "1";
  } catch {
    return false;
  }
}

function saveLater(userId) {
  try {
    sessionStorage.setItem(laterKey(userId), "1");
  } catch {
  }
}

/**
 * @param {boolean}  props.enabled   a student is signed in
 * @param {Function} [props.onOpen]  called just before a survey appears (e.g. to stop app audio)
 */
export default function SurveyGate({ enabled, userId, api = defaultApi, audioPlayer, onOpen, children = null }) {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  const [exited, setExited] = useState(false);
  const dismissed = exited || (Boolean(userId) && readLater(userId));
  const finished = useRef(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const openNext = useCallback(async () => {
    setChecking(true);
    try {
      const pending = await api.pending();
      if (!pending?.length) return setSession(null);
      finished.current = false;
      const next = await api.start(pending[0].assignmentId);
      onOpenRef.current?.();
      setSession(next);
    } catch {
      setSession(null);
    } finally {
      setChecking(false);
      setChecked(true);
    }
  }, [api]);

  useEffect(() => {
    if (enabled && !dismissed) openNext();
  }, [enabled, dismissed, openNext]);

  const waiting = enabled && !dismissed && (!checked || checking) && !session;
  if (!enabled || dismissed || (!session && !waiting)) return children;
  if (!session) return <div className="r21-survey-gate" aria-busy="true" data-testid="survey-checking" />;

  return (
    <div className="r21-survey-gate" role="dialog" aria-modal="true" aria-label={session.survey.title}>
      <SurveyPlayer
        key={session.responseId}
        session={session}
        audioPlayer={audioPlayer}
        onAnswer={(questionId, value) => api.answer(session.responseId, questionId, value)}
        onComplete={async () => {
          await api.complete(session.responseId);
          finished.current = true;
        }}
        onExit={() => {
          if (finished.current) {
            setSession(null);
            openNext();
          } else {
            saveLater(userId);
            setExited(true);
          }
        }}
      />
    </div>
  );
}
